import { useEffect, useState } from 'react';
import axios from 'axios';
import { Save, UserPlus, Camera, Trash2, Calendar, MousePointerClick, Users, History, RefreshCw, ChevronLeft, ChevronRight, Plus, MessageSquare, Globe, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_T1_COUNTRIES = ['US', 'GB', 'CA', 'AU', 'FR', 'DE', 'BE', 'IT'];

// Networks a traffic source can come from. GMS only tracks T1 clicks for
// Threads, so that one defaults to keeping the click stage of the funnel.
const NETWORK_OPTIONS = [
  { value: 'threads', label: 'Threads' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'x', label: 'X / Twitter' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'other', label: 'Other' },
];

const EMPTY_SOURCE = { id: '', name: '', network: 'threads', model_id: '', account_ids: '', t1_tracked: true };

// Account IDs are pasted freely: one per line, comma or space separated.
const parseAccountIds = (raw) =>
  String(raw || '')
    .split(/[\s,;]+/)
    .map(v => v.trim())
    .filter(Boolean);
const ADMIN_PIN = '3724';

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [openAdminCreate, setOpenAdminCreate] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '', pin: '' });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  
  // Snapshot states
  const [snapshots, setSnapshots] = useState([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [snapshotTimeframe, setSnapshotTimeframe] = useState('yesterday');
  const [openSnapshotDialog, setOpenSnapshotDialog] = useState(false);
  const [backfillDays, setBackfillDays] = useState(30);
  const [backfilling, setBackfilling] = useState(false);
  const [openBackfillDialog, setOpenBackfillDialog] = useState(false);
  const [backfillOverwrite, setBackfillOverwrite] = useState(false);
  
  // Pagination states
  const [snapshotPage, setSnapshotPage] = useState(1);
  const [snapshotTotal, setSnapshotTotal] = useState(0);
  const [snapshotTotalPages, setSnapshotTotalPages] = useState(1);
  const SNAPSHOTS_PER_PAGE = 15;
  
  // CupidBot models states
  const [cupidBotModels, setCupidBotModels] = useState([]);
  const [newModel, setNewModel] = useState({ id: '', name: '' });
  const [openAddModelDialog, setOpenAddModelDialog] = useState(false);
  
  // CupidBot traffic sources states (one social profile -> N Telegram accounts)
  const [cupidBotSources, setCupidBotSources] = useState([]);
  const [sourceDialog, setSourceDialog] = useState({ open: false, mode: 'add', index: -1 });
  const [sourceForm, setSourceForm] = useState(EMPTY_SOURCE);
  
  // CupidBot snapshots states
  const [cupidBotSnapshots, setCupidBotSnapshots] = useState([]);
  const [loadingCupidBotSnapshots, setLoadingCupidBotSnapshots] = useState(false);
  const [cupidBotSnapshotPage, setCupidBotSnapshotPage] = useState(1);
  const [cupidBotSnapshotTotal, setCupidBotSnapshotTotal] = useState(0);
  const [cupidBotSnapshotTotalPages, setCupidBotSnapshotTotalPages] = useState(1);
  const [expandedCupidBotSnapshot, setExpandedCupidBotSnapshot] = useState(null);

  useEffect(() => {
    fetchSettings();
    if (user?.role === 'admin') {
      fetchSnapshots(1);
      fetchCupidBotSnapshots(1);
      fetchCacheStatus();
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(response.data);
      setCupidBotModels(response.data.cupidbot_tracked_models || []);
      setCupidBotSources(response.data.cupidbot_traffic_sources || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const saveTrafficSources = async (updated) => {
    try {
      await axios.put(`${API_URL}/settings`, { cupidbot_traffic_sources: updated });
      setCupidBotSources(updated);
      return true;
    } catch (error) {
      console.error('Error saving traffic sources:', error);
      toast.error('Failed to save traffic sources');
      return false;
    }
  };

  const openAddSource = () => {
    setSourceForm(EMPTY_SOURCE);
    setSourceDialog({ open: true, mode: 'add', index: -1 });
  };

  const openEditSource = (source, index) => {
    setSourceForm({
      id: source.id,
      name: source.name || '',
      network: source.network || 'other',
      model_id: source.model_id || '',
      account_ids: (source.account_ids || []).join('\n'),
      t1_tracked: !!source.t1_tracked,
    });
    setSourceDialog({ open: true, mode: 'edit', index });
  };

  const submitSource = async () => {
    const name = sourceForm.name.trim();
    const accountIds = parseAccountIds(sourceForm.account_ids);

    if (!name) {
      toast.error('Profile name is required');
      return;
    }
    if (accountIds.length === 0) {
      toast.error('Add at least one Telegram account ID');
      return;
    }

    const model = cupidBotModels.find(m => m.id === sourceForm.model_id);
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const entry = {
      id: sourceForm.id || `${slug || 'source'}-${Date.now().toString(36)}`,
      name,
      network: sourceForm.network,
      account_ids: accountIds,
      model_id: sourceForm.model_id || null,
      model_name: model?.name || null,
      t1_tracked: !!sourceForm.t1_tracked,
    };

    const updated = sourceDialog.mode === 'edit'
      ? cupidBotSources.map((s, i) => (i === sourceDialog.index ? entry : s))
      : [...cupidBotSources, entry];

    if (await saveTrafficSources(updated)) {
      setSourceDialog({ open: false, mode: 'add', index: -1 });
      setSourceForm(EMPTY_SOURCE);
      toast.success(`Traffic source "${name}" saved`);
    }
  };

  const deleteSource = async (index, name) => {
    if (!window.confirm(`Remove traffic source "${name}"?`)) return;
    const updated = cupidBotSources.filter((_, i) => i !== index);
    if (await saveTrafficSources(updated)) {
      toast.success(`Traffic source "${name}" removed`);
    }
  };

  const fetchSnapshots = async (page = 1) => {
    setLoadingSnapshots(true);
    try {
      const response = await axios.get(`${API_URL}/snapshots?page=${page}&limit=${SNAPSHOTS_PER_PAGE}`);
      setSnapshots(response.data.snapshots || []);
      setSnapshotTotal(response.data.total || 0);
      setSnapshotTotalPages(response.data.total_pages || 1);
      setSnapshotPage(page);
    } catch (error) {
      console.error('Error fetching snapshots:', error);
    } finally {
      setLoadingSnapshots(false);
    }
  };

  const fetchCupidBotSnapshots = async (page = 1) => {
    setLoadingCupidBotSnapshots(true);
    try {
      const response = await axios.get(`${API_URL}/cupidbot/snapshots?page=${page}&page_size=${SNAPSHOTS_PER_PAGE}`);
      setCupidBotSnapshots(response.data.snapshots || []);
      setCupidBotSnapshotTotal(response.data.total || 0);
      setCupidBotSnapshotTotalPages(response.data.total_pages || 1);
      setCupidBotSnapshotPage(page);
    } catch (error) {
      console.error('Error fetching CupidBot snapshots:', error);
    } finally {
      setLoadingCupidBotSnapshots(false);
    }
  };

  const handleCreateSnapshot = async () => {
    setCreatingSnapshot(true);
    try {
      const response = await axios.post(`${API_URL}/snapshots/create`, {
        timeframe: snapshotTimeframe
      });
      toast.success(response.data.message || 'Snapshot created successfully');
      setOpenSnapshotDialog(false);
      fetchSnapshots(1); // Go back to first page to see new snapshot
    } catch (error) {
      console.error('Error creating snapshot:', error);
      toast.error(error.response?.data?.detail || 'Failed to create snapshot');
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const handleDeleteSnapshot = async (date) => {
    if (!window.confirm(`Delete snapshot for ${date}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/snapshots/${date}`);
      toast.success('Snapshot deleted');
      fetchSnapshots(snapshotPage); // Stay on current page
    } catch (error) {
      console.error('Error deleting snapshot:', error);
      toast.error('Failed to delete snapshot');
    }
  };

  // Today Cache state (admin force refresh)
  const [cacheStatus, setCacheStatus] = useState(null);
  const [refreshingCache, setRefreshingCache] = useState(false);

  const fetchCacheStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/cache/status`);
      setCacheStatus(res.data);
    } catch (e) {
      // silent
    }
  };

  const handleForceRefreshCache = async () => {
    if (!window.confirm(
      'Force refresh will BYPASS the safety guard and can lower total clicks. Use only if you know today\'s cache is inflated with bad data. Continue?'
    )) return;
    setRefreshingCache(true);
    try {
      await axios.post(`${API_URL}/cache/refresh?force=true`, {});
      toast.success('Force refresh started in background. Data will update in ~1-2 minutes.');
      // Refresh status after 90s
      setTimeout(() => fetchCacheStatus(), 90_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start force refresh');
    } finally {
      setRefreshingCache(false);
    }
  };

  const handleNormalRefreshCache = async () => {
    setRefreshingCache(true);
    try {
      await axios.post(`${API_URL}/cache/refresh`, {});
      toast.success('Cache refresh started in background.');
      setTimeout(() => fetchCacheStatus(), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start refresh');
    } finally {
      setRefreshingCache(false);
    }
  };

  const handleBackfill = async () => {    setBackfilling(true);
    try {
      const response = await axios.post(`${API_URL}/snapshots/backfill`, {
        days_back: backfillDays,
        overwrite: backfillOverwrite
      });
      toast.success(response.data.message || 'Backfill started');
      setOpenBackfillDialog(false);
      setBackfillOverwrite(false); // Reset checkbox
      // Refresh snapshots after a delay to show progress
      setTimeout(() => fetchSnapshots(1), 3000);
    } catch (error) {
      console.error('Error starting backfill:', error);
      toast.error(error.response?.data?.detail || 'Failed to start backfill');
    } finally {
      setBackfilling(false);
    }
  };

  const [recreateDialogOpen, setRecreateDialogOpen] = useState(false);
  const [recreateDialogState, setRecreateDialogState] = useState({ date: null, status: null, force: false });

  const openRecreateDialog = async (date) => {
    setRecreateDialogState({ date, status: null, force: false });
    setRecreateDialogOpen(true);
    // Load last known status
    try {
      const res = await axios.get(`${API_URL}/snapshots/recreate-status/${date}`);
      setRecreateDialogState((prev) => ({ ...prev, status: res.data }));
    } catch (e) {
      // No previous job, ok
    }
  };

  const startRecreate = async () => {
    const { date, force } = recreateDialogState;
    if (!date) return;
    try {
      toast.info(`Starting snapshot recreation for ${date}${force ? ' (FORCE mode)' : ''}...`);
      await axios.post(`${API_URL}/snapshots/recreate`, { date, force });
      // Poll every 5s
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_URL}/snapshots/recreate-status/${date}`);
          setRecreateDialogState((prev) => ({ ...prev, status: statusRes.data }));
          if (statusRes.data.status === 'completed' || statusRes.data.status === 'failed') {
            clearInterval(pollInterval);
            if (statusRes.data.status === 'completed') {
              toast.success(`Snapshot recreated: ${statusRes.data.result?.total_t1_clicks || 0} T1 clicks`);
              fetchSnapshots(snapshotPage);
            } else {
              toast.error(`Rejected: ${statusRes.data.error || 'Unknown'}`);
            }
          }
        } catch (e) { /* keep polling */ }
      }, 5000);
      setTimeout(() => clearInterval(pollInterval), 900000); // 15 min max
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start recreate');
    }
  };

  const handleRecreateSnapshot = async (date) => {
    openRecreateDialog(date);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API_URL}/settings`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (newAdmin.pin !== ADMIN_PIN) {
      toast.error('Invalid PIN code');
      return;
    }
    
    setCreatingAdmin(true);
    
    try {
      await axios.post(`${API_URL}/auth/register`, {
        name: newAdmin.name,
        email: newAdmin.email,
        password: newAdmin.password,
        role: 'admin'
      });
      toast.success('Admin account created successfully');
      setOpenAdminCreate(false);
      setNewAdmin({ name: '', email: '', password: '', pin: '' });
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to create admin account');
    } finally {
      setCreatingAdmin(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="settings-title">
            Settings
          </h1>
          <p className="text-base text-muted-foreground mt-2">Configure system parameters</p>
        </div>
        
        <Dialog open={openAdminCreate} onOpenChange={setOpenAdminCreate}>
          <DialogTrigger asChild>
            <Button className="bg-white text-black hover:bg-zinc-200" data-testid="create-admin-button">
              <UserPlus className="w-4 h-4 mr-2" />
              Create Admin
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle className="text-white">Create Admin Account</DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Create a new administrator account (PIN required)
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-name" className="text-white">Name</Label>
                <Input
                  id="admin-name"
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                  required
                  className="bg-zinc-900/50 border-zinc-800 text-white"
                  data-testid="admin-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-email" className="text-white">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  required
                  className="bg-zinc-900/50 border-zinc-800 text-white"
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password" className="text-white">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  required
                  className="bg-zinc-900/50 border-zinc-800 text-white"
                  data-testid="admin-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-pin" className="text-white">PIN Code (4 digits)</Label>
                <Input
                  id="admin-pin"
                  type="password"
                  maxLength={4}
                  value={newAdmin.pin}
                  onChange={(e) => setNewAdmin({ ...newAdmin, pin: e.target.value })}
                  required
                  className="bg-zinc-900/50 border-zinc-800 text-white"
                  placeholder="Enter 4-digit PIN"
                  data-testid="admin-pin-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-white text-black hover:bg-zinc-200"
                disabled={creatingAdmin}
                data-testid="admin-create-submit-button"
              >
                {creatingAdmin ? 'Creating...' : 'Create Admin'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="bg-card p-6 rounded-md border border-border space-y-6">
          <div>
            <h2 className="text-2xl font-semibold font-heading text-white mb-4">Payment Configuration</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="price-per-click" className="text-white">Price per T1 Click ($)</Label>
                <Input
                  id="price-per-click"
                  type="number"
                  step="0.01"
                  value={settings?.price_per_click || 0}
                  onChange={(e) => setSettings({ ...settings, price_per_click: parseFloat(e.target.value) })}
                  className="bg-zinc-900/50 border-zinc-800 text-white"
                  data-testid="price-per-click-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bonus-threshold" className="text-white">Bonus Threshold (Clicks)</Label>
                <Input
                  id="bonus-threshold"
                  type="number"
                  value={settings?.bonus_threshold || 0}
                  onChange={(e) => setSettings({ ...settings, bonus_threshold: parseInt(e.target.value) })}
                  className="bg-zinc-900/50 border-zinc-800 text-white"
                  data-testid="bonus-threshold-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bonus-amount" className="text-white">Bonus Amount ($)</Label>
                <Input
                  id="bonus-amount"
                  type="number"
                  step="0.01"
                  value={settings?.bonus_amount || 0}
                  onChange={(e) => setSettings({ ...settings, bonus_amount: parseFloat(e.target.value) })}
                  className="bg-zinc-900/50 border-zinc-800 text-white"
                  data-testid="bonus-amount-input"
                />
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold font-heading text-white mb-4">T1 Countries</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Countries considered as Tier 1 (comma-separated country codes)
            </p>
            <Input
              value={settings?.t1_countries?.join(', ') || ''}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  t1_countries: e.target.value.split(',').map((c) => c.trim().toUpperCase()),
                })
              }
              className="bg-zinc-900/50 border-zinc-800 text-white"
              placeholder="US, GB, CA, AU, FR, DE, BE, IT"
              data-testid="t1-countries-input"
            />
          </div>
        </div>

        <Button
          type="submit"
          className="bg-white text-black hover:bg-zinc-200"
          disabled={saving}
          data-testid="save-settings-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </form>

      {/* CupidBot Configuration - Admin only */}
      {user?.role === 'admin' && (
        <div className="bg-card p-6 rounded-md border border-border space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-semibold font-heading text-white flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-purple-500" />
                CupidBot Configuration
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Configure which CupidBot models/creators to track for analytics
              </p>
            </div>
            
            <Dialog open={openAddModelDialog} onOpenChange={setOpenAddModelDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800" data-testid="add-model-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Model
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
                <DialogHeader>
                  <DialogTitle className="text-white">Add CupidBot Model</DialogTitle>
                  <DialogDescription className="text-muted-foreground">
                    Add a model/creator to track in CupidBot analytics. You need the creator ID from CupidBot.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white">Model/Creator ID</Label>
                    <Input
                      value={newModel.id}
                      onChange={(e) => setNewModel({ ...newModel, id: e.target.value })}
                      className="bg-zinc-900/50 border-zinc-800 text-white"
                      placeholder="e.g., abc123def456"
                      data-testid="new-model-id-input"
                    />
                    <p className="text-xs text-zinc-500">
                      The viewingCreatorID from CupidBot API
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Display Name</Label>
                    <Input
                      value={newModel.name}
                      onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                      className="bg-zinc-900/50 border-zinc-800 text-white"
                      placeholder="e.g., Luna, Hana"
                      data-testid="new-model-name-input"
                    />
                  </div>
                  <Button
                    onClick={async () => {
                      if (!newModel.id || !newModel.name) {
                        toast.error('Both ID and Name are required');
                        return;
                      }
                      
                      const updatedModels = [...cupidBotModels, { id: newModel.id, name: newModel.name }];
                      
                      try {
                        await axios.put(`${API_URL}/settings`, {
                          cupidbot_tracked_models: updatedModels
                        });
                        setCupidBotModels(updatedModels);
                        setNewModel({ id: '', name: '' });
                        setOpenAddModelDialog(false);
                        toast.success(`Model "${newModel.name}" added`);
                      } catch (error) {
                        toast.error('Failed to save model');
                      }
                    }}
                    className="w-full bg-white text-black hover:bg-zinc-200"
                    data-testid="save-new-model-button"
                  >
                    Add Model
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {/* Models List */}
          {cupidBotModels.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No models configured yet.</p>
              <p className="text-sm">Add models to start tracking their analytics.</p>
            </div>
          ) : (
            <div className="border border-zinc-800 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400">Name</TableHead>
                    <TableHead className="text-zinc-400">Creator ID</TableHead>
                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cupidBotModels.map((model, index) => (
                    <TableRow key={model.id} className="border-zinc-800">
                      <TableCell className="font-medium text-white">{model.name}</TableCell>
                      <TableCell className="text-zinc-400 font-mono text-sm">{model.id}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            if (!window.confirm(`Remove model "${model.name}"?`)) return;
                            
                            const updatedModels = cupidBotModels.filter((_, i) => i !== index);
                            
                            try {
                              await axios.put(`${API_URL}/settings`, {
                                cupidbot_tracked_models: updatedModels
                              });
                              setCupidBotModels(updatedModels);
                              toast.success(`Model "${model.name}" removed`);
                            } catch (error) {
                              toast.error('Failed to remove model');
                            }
                          }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-model-${model.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* CupidBot Traffic Sources - Admin only */}
      {user?.role === 'admin' && (
        <div className="bg-card p-6 rounded-md border border-border space-y-6" data-testid="traffic-sources-section">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-semibold font-heading text-white flex items-center gap-2">
                <Globe className="w-6 h-6 text-blue-500" />
                Traffic Sources
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                One entry per social profile (e.g. "Nyx Threads"), listing the CupidBot
                Telegram account IDs it feeds. Analytics for a source sum all its accounts.
              </p>
            </div>

            <Button
              variant="outline"
              onClick={openAddSource}
              className="border-zinc-700 text-white hover:bg-zinc-800"
              data-testid="add-source-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Source
            </Button>
          </div>

          {cupidBotSources.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No traffic sources configured yet.</p>
              <p className="text-sm">Add a profile to filter analytics by network.</p>
            </div>
          ) : (
            <div className="border border-zinc-800 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400">Profile</TableHead>
                    <TableHead className="text-zinc-400">Network</TableHead>
                    <TableHead className="text-zinc-400">Model</TableHead>
                    <TableHead className="text-zinc-400">Telegram Accounts</TableHead>
                    <TableHead className="text-zinc-400">T1</TableHead>
                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cupidBotSources.map((source, index) => (
                    <TableRow key={source.id} className="border-zinc-800">
                      <TableCell className="font-medium text-white">{source.name}</TableCell>
                      <TableCell className="text-zinc-400">
                        {NETWORK_OPTIONS.find(n => n.value === source.network)?.label || source.network}
                      </TableCell>
                      <TableCell className="text-zinc-400">{source.model_name || '—'}</TableCell>
                      <TableCell className="text-zinc-400 font-mono text-xs max-w-[280px]">
                        <span className="text-white">{source.account_ids?.length || 0}</span>
                        {' · '}
                        {(source.account_ids || []).join(', ')}
                      </TableCell>
                      <TableCell>
                        {source.t1_tracked ? (
                          <span className="text-xs text-green-400">tracked</span>
                        ) : (
                          <span className="text-xs text-zinc-600">n/a</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditSource(source, index)}
                          className="text-zinc-400 hover:text-white hover:bg-zinc-800"
                          data-testid={`edit-source-${source.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteSource(index, source.name)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-source-${source.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Add / Edit dialog */}
          <Dialog
            open={sourceDialog.open}
            onOpenChange={(open) => setSourceDialog(prev => ({ ...prev, open }))}
          >
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {sourceDialog.mode === 'edit' ? 'Edit Traffic Source' : 'Add Traffic Source'}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Paste the CupidBot Telegram account IDs fed by this profile — one per
                  line, or separated by commas.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Profile Name</Label>
                  <Input
                    value={sourceForm.name}
                    onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })}
                    className="bg-zinc-900/50 border-zinc-800 text-white"
                    placeholder="e.g., Nyx Threads"
                    data-testid="source-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Network</Label>
                  <Select
                    value={sourceForm.network}
                    onValueChange={(value) => setSourceForm({
                      ...sourceForm,
                      network: value,
                      // GMS only tracks T1 clicks on Threads traffic.
                      t1_tracked: value === 'threads',
                    })}
                  >
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white" data-testid="source-network-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      {NETWORK_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Model</Label>
                  <Select
                    value={sourceForm.model_id || 'none'}
                    onValueChange={(value) => setSourceForm({
                      ...sourceForm,
                      model_id: value === 'none' ? '' : value,
                    })}
                  >
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white" data-testid="source-model-select">
                      <SelectValue placeholder="No model" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectItem value="none">No model</SelectItem>
                      {cupidBotModels.map(model => (
                        <SelectItem key={model.id} value={model.id}>
                          {model.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Telegram Account IDs</Label>
                  <textarea
                    value={sourceForm.account_ids}
                    onChange={(e) => setSourceForm({ ...sourceForm, account_ids: e.target.value })}
                    rows={5}
                    className="w-full bg-zinc-900/50 border border-zinc-800 text-white rounded-md px-3 py-2 text-sm font-mono"
                    placeholder={'7959840286\n8170055486\n8029318370'}
                    data-testid="source-accounts-input"
                  />
                  <p className="text-xs text-zinc-500">
                    {parseAccountIds(sourceForm.account_ids).length} account(s) detected
                  </p>
                </div>

                <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sourceForm.t1_tracked}
                    onChange={(e) => setSourceForm({ ...sourceForm, t1_tracked: e.target.checked })}
                    className="accent-blue-500"
                    data-testid="source-t1-checkbox"
                  />
                  T1 clicks are tracked for this network
                </label>
              </div>

              <DialogFooter>
                <Button
                  onClick={submitSource}
                  className="w-full bg-white text-black hover:bg-zinc-200"
                  data-testid="save-source-button"
                >
                  {sourceDialog.mode === 'edit' ? 'Save Changes' : 'Add Source'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Today Cache Section - Admin only */}
      {user?.role === 'admin' && (
        <div className="bg-card p-6 rounded-md border border-border space-y-4" data-testid="today-cache-section">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-semibold font-heading text-white">Today Cache</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Live GetMySocial cache for today (UTC+0). Auto-refreshes every hour.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleNormalRefreshCache}
                disabled={refreshingCache}
                className="border-zinc-700 text-white hover:bg-zinc-800"
                data-testid="cache-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingCache ? 'animate-spin' : ''}`} />
                Refresh now
              </Button>
              <Button
                variant="outline"
                onClick={handleForceRefreshCache}
                disabled={refreshingCache}
                className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                data-testid="cache-force-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingCache ? 'animate-spin' : ''}`} />
                Force refresh (bypass guard)
              </Button>
            </div>
          </div>

          {cacheStatus && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-md border border-zinc-800 bg-zinc-950">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Date (UTC)</div>
                <div className="text-sm font-mono text-white mt-1">{cacheStatus.date || '—'}</div>
              </div>
              <div className="p-3 rounded-md border border-zinc-800 bg-zinc-950">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total T1 clicks</div>
                <div className="text-lg font-bold text-emerald-400 mt-1">
                  {(cacheStatus.total_t1_clicks || 0).toLocaleString()}
                </div>
              </div>
              <div className="p-3 rounded-md border border-zinc-800 bg-zinc-950">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total wasted</div>
                <div className="text-lg font-bold text-red-400 mt-1">
                  {(cacheStatus.total_wasted_clicks || 0).toLocaleString()}
                </div>
              </div>
              <div className="p-3 rounded-md border border-zinc-800 bg-zinc-950">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Last updated</div>
                <div className="text-xs text-white mt-1">
                  {cacheStatus.last_updated ? new Date(cacheStatus.last_updated).toLocaleString() : '—'}
                </div>
              </div>
            </div>
          )}

          <div className="text-xs text-zinc-500 border-l-2 border-zinc-700 pl-3">
            <strong>Safety guard</strong>: normal refresh blocks any refresh that would lower today&apos;s total
            (protects against partial GMS API responses).
            <strong className="text-red-400 ml-1">Force refresh</strong> bypasses this — use only if today&apos;s
            total is stuck too high due to bad past data.
          </div>
        </div>
      )}

      {/* Data Snapshots Section - Admin only */}
      {user?.role === 'admin' && (
        <div className="bg-card p-6 rounded-md border border-border space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-semibold font-heading text-white">Data Snapshots</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Historical data is saved automatically each day at 00:30 UTC. Use backfill to capture past days.
              </p>
            </div>
            
            <div className="flex gap-2">
              {/* Backfill Button */}
              <Dialog open={openBackfillDialog} onOpenChange={setOpenBackfillDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800" data-testid="backfill-button">
                    <History className="w-4 h-4 mr-2" />
                    Backfill Days
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
                  <DialogHeader>
                    <DialogTitle className="text-white">Backfill Historical Data</DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                      Create snapshots for each day going back in time.
                      Fetches data from GetMySocial for each past day individually.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-white">Number of days to backfill</Label>
                      <Input
                        type="number"
                        min="1"
                        max="90"
                        value={backfillDays}
                        onChange={(e) => setBackfillDays(Math.min(90, Math.max(1, parseInt(e.target.value) || 30)))}
                        className="bg-zinc-900/50 border-zinc-800 text-white"
                        data-testid="backfill-days-input"
                      />
                      <p className="text-xs text-zinc-500">
                        Creates {backfillDays} snapshots (one per day from yesterday back).
                        Max 90 days.
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="overwrite-snapshots"
                        checked={backfillOverwrite}
                        onChange={(e) => setBackfillOverwrite(e.target.checked)}
                        className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-orange-500 focus:ring-orange-500"
                      />
                      <label htmlFor="overwrite-snapshots" className="text-sm text-white cursor-pointer">
                        Overwrite existing snapshots
                      </label>
                    </div>
                    
                    {backfillOverwrite && (
                      <div className="bg-orange-900/30 border border-orange-700/50 rounded p-3">
                        <p className="text-sm text-orange-200">
                          <strong>Overwrite mode:</strong> All existing snapshots will be recreated with fresh data (includes new links).
                        </p>
                      </div>
                    )}
                    
                    <div className="bg-amber-900/20 border border-amber-700/50 rounded p-3">
                      <p className="text-sm text-amber-200">
                        <strong>Note:</strong> This may take several minutes (~2s per day). Runs in background - refresh to see progress.
                      </p>
                    </div>
                    
                    <Button
                      onClick={handleBackfill}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      disabled={backfilling}
                      data-testid="confirm-backfill"
                    >
                      {backfilling ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <History className="w-4 h-4 mr-2" />
                          Start Backfill ({backfillDays} days)
                        </>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Create Snapshot Button */}
              <Dialog open={openSnapshotDialog} onOpenChange={setOpenSnapshotDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-snapshot-button">
                    <Camera className="w-4 h-4 mr-2" />
                    Create Snapshot
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
                <DialogHeader>
                  <DialogTitle className="text-white">Create Data Snapshot</DialogTitle>
                  <DialogDescription className="text-muted-foreground">
                    Save current data from GetMySocial as a historical snapshot. 
                    This data will be used for "Yesterday", "Last Week", etc. views.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white">Timeframe to capture</Label>
                    <Select value={snapshotTimeframe} onValueChange={setSnapshotTimeframe}>
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="yesterday">Yesterday</SelectItem>
                        <SelectItem value="last-week">Last Week (aggregated)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-zinc-500">
                      {snapshotTimeframe === 'today' && 'Captures today\'s data so far'}
                      {snapshotTimeframe === 'yesterday' && 'Captures yesterday\'s full data'}
                      {snapshotTimeframe === 'last-week' && 'Captures all of last week\'s data as a single snapshot'}
                    </p>
                  </div>
                  
                  <Button
                    onClick={handleCreateSnapshot}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={creatingSnapshot}
                    data-testid="confirm-create-snapshot"
                  >
                    {creatingSnapshot ? 'Creating Snapshot...' : 'Create Snapshot'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Snapshots List */}
          <div>
            {loadingSnapshots ? (
              <div className="text-center py-4 text-zinc-400">Loading snapshots...</div>
            ) : snapshots.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-zinc-700 rounded-md">
                <Camera className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">No snapshots yet</p>
                <p className="text-sm text-zinc-500 mt-1">
                  Create your first snapshot to start saving historical data
                </p>
              </div>
            ) : (
              <>
                <div className="border border-zinc-800 rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Date</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">VAs</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">T1 Clicks</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Wasted</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Earnings</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {snapshots.map((snap) => (
                        <TableRow key={snap.date} className="border-b border-zinc-800">
                          <TableCell className="font-medium text-white">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-zinc-500" />
                              {snap.date}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-orange-400">
                              <Users className="w-3 h-3" />
                              {snap.total_vas}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-emerald-400">
                              <MousePointerClick className="w-3 h-3" />
                              {snap.total_t1_clicks?.toLocaleString()}
                            </div>
                          </TableCell>
                          <TableCell className="text-red-400">
                            {snap.total_wasted_clicks?.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-white font-medium">
                            ${snap.total_earnings?.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRecreateSnapshot(snap.date)}
                                className="text-blue-500 hover:text-blue-400 hover:bg-blue-900/20"
                                title="Recreate snapshot (fetch fresh data)"
                                data-testid={`recreate-snapshot-${snap.date}`}
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSnapshot(snap.date)}
                                className="text-red-500 hover:text-red-400 hover:bg-red-900/20"
                                data-testid={`delete-snapshot-${snap.date}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination */}
                {snapshotTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-zinc-400">
                      Showing {((snapshotPage - 1) * SNAPSHOTS_PER_PAGE) + 1} - {Math.min(snapshotPage * SNAPSHOTS_PER_PAGE, snapshotTotal)} of {snapshotTotal} snapshots
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchSnapshots(snapshotPage - 1)}
                        disabled={snapshotPage <= 1 || loadingSnapshots}
                        className="border-zinc-700 text-white hover:bg-zinc-800 disabled:opacity-50"
                        data-testid="snapshot-prev-page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-white px-2">
                        Page {snapshotPage} / {snapshotTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchSnapshots(snapshotPage + 1)}
                        disabled={snapshotPage >= snapshotTotalPages || loadingSnapshots}
                        className="border-zinc-700 text-white hover:bg-zinc-800 disabled:opacity-50"
                        data-testid="snapshot-next-page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* CupidBot Snapshots Section - Admin only */}
      {user?.role === 'admin' && (
        <div className="bg-card p-6 rounded-md border border-border space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-semibold font-heading text-white flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-purple-500" />
                CupidBot Snapshots
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Daily snapshots of CupidBot conversations and conversions. Created automatically at 00:35 UTC.
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            {loadingCupidBotSnapshots ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
              </div>
            ) : cupidBotSnapshots.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No CupidBot snapshots yet.</p>
                <p className="text-sm">Snapshots are created automatically each day at 00:35 UTC.</p>
              </div>
            ) : (
              <>
                <div className="border border-zinc-800 rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Date</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Conversations</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Conversions</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">CVR %</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Models</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cupidBotSnapshots.map((snap) => (
                        <>
                          <TableRow 
                            key={snap.date} 
                            className="border-b border-zinc-800 cursor-pointer hover:bg-zinc-800/50"
                            onClick={() => setExpandedCupidBotSnapshot(expandedCupidBotSnapshot === snap.date ? null : snap.date)}
                          >
                            <TableCell className="font-medium text-white">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-zinc-500" />
                                {snap.date}
                                {snap.model_data?.length > 0 && (
                                  <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${expandedCupidBotSnapshot === snap.date ? 'rotate-90' : ''}`} />
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-green-400">
                                <MessageSquare className="w-3 h-3" />
                                {snap.conversations_charged?.toLocaleString()}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-amber-400">
                                {snap.conversions?.toLocaleString()}
                              </div>
                            </TableCell>
                            <TableCell className="text-purple-400">
                              {snap.conversations_charged > 0 
                                ? ((snap.conversions / snap.conversations_charged) * 100).toFixed(2) + '%'
                                : '0%'}
                            </TableCell>
                            <TableCell className="text-zinc-400">
                              {snap.model_data?.length || 0} models
                            </TableCell>
                          </TableRow>
                          
                          {/* Expanded model data */}
                          {expandedCupidBotSnapshot === snap.date && snap.model_data?.length > 0 && (
                            <TableRow className="border-b border-zinc-800 bg-zinc-900/50">
                              <TableCell colSpan={5} className="p-4">
                                <div className="space-y-2">
                                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Per-Model Breakdown</p>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {snap.model_data.map((model) => (
                                      <div 
                                        key={model.model_id} 
                                        className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3"
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="font-medium text-white">{model.model_name}</span>
                                          <span className="text-xs text-zinc-500">ID: {model.model_id}</span>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 text-sm">
                                          <div>
                                            <p className="text-zinc-500">Conversations</p>
                                            <p className="text-green-400 font-medium">{model.conversations_charged?.toLocaleString()}</p>
                                          </div>
                                          <div>
                                            <p className="text-zinc-500">Conversions</p>
                                            <p className="text-amber-400 font-medium">{model.conversions?.toLocaleString()}</p>
                                          </div>
                                          <div>
                                            <p className="text-zinc-500">CVR</p>
                                            <p className="text-purple-400 font-medium">
                                              {model.conversations_charged > 0 
                                                ? ((model.conversions / model.conversations_charged) * 100).toFixed(2) + '%'
                                                : '0%'}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* CupidBot Pagination */}
                {cupidBotSnapshotTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-zinc-400">
                      Showing {((cupidBotSnapshotPage - 1) * SNAPSHOTS_PER_PAGE) + 1} - {Math.min(cupidBotSnapshotPage * SNAPSHOTS_PER_PAGE, cupidBotSnapshotTotal)} of {cupidBotSnapshotTotal} snapshots
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchCupidBotSnapshots(cupidBotSnapshotPage - 1)}
                        disabled={cupidBotSnapshotPage <= 1 || loadingCupidBotSnapshots}
                        className="border-zinc-700 text-white hover:bg-zinc-800 disabled:opacity-50"
                        data-testid="cupidbot-snapshot-prev-page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-white px-2">
                        Page {cupidBotSnapshotPage} / {cupidBotSnapshotTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchCupidBotSnapshots(cupidBotSnapshotPage + 1)}
                        disabled={cupidBotSnapshotPage >= cupidBotSnapshotTotalPages || loadingCupidBotSnapshots}
                        className="border-zinc-700 text-white hover:bg-zinc-800 disabled:opacity-50"
                        data-testid="cupidbot-snapshot-next-page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Snapshot Recreate Debug Dialog */}
      <Dialog open={recreateDialogOpen} onOpenChange={setRecreateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Recreate snapshot — {recreateDialogState.date}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Fetches fresh data from GetMySocial. Can take 5-10 min. Safety guard rejects a &gt;25% drop unless &ldquo;Force&rdquo; is enabled.
            </DialogDescription>
          </DialogHeader>

          {/* Current status */}
          {recreateDialogState.status && (
            <div className="space-y-3 p-4 rounded-md bg-zinc-950 border border-zinc-800">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase text-zinc-500">Job status</span>
                <span className={`text-sm font-mono px-2 py-0.5 rounded ${
                  recreateDialogState.status.status === 'completed' ? 'bg-emerald-500/20 text-emerald-300' :
                  recreateDialogState.status.status === 'failed' ? 'bg-red-500/20 text-red-300' :
                  recreateDialogState.status.status === 'processing' ? 'bg-blue-500/20 text-blue-300' :
                  'bg-zinc-500/20 text-zinc-300'
                }`} data-testid="recreate-status-badge">
                  {recreateDialogState.status.status || 'no previous job'}
                </span>
              </div>
              {recreateDialogState.status.result && (
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div><span className="text-zinc-500">T1: </span><span className="text-white font-semibold">{recreateDialogState.status.result.total_t1_clicks ?? '—'}</span></div>
                  <div><span className="text-zinc-500">Wasted: </span><span className="text-white font-semibold">{recreateDialogState.status.result.total_wasted_clicks ?? '—'}</span></div>
                  <div><span className="text-zinc-500">VAs: </span><span className="text-white font-semibold">{recreateDialogState.status.result.total_vas ?? '—'}</span></div>
                </div>
              )}
              <div className="text-xs">
                <span className="text-zinc-500">Fallback GMS keys available: </span>
                <span className={`font-mono ${recreateDialogState.status.fallback_keys_available > 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="recreate-fallback-keys">
                  {recreateDialogState.status.fallback_keys_available ?? 'unknown'}
                </span>
                {recreateDialogState.status.fallback_keys_available === 0 && (
                  <span className="text-red-400 ml-2">
                    ⚠️ GMS_API_KEY_SNAPSHOT_2 not loaded — no rotation on rate limit
                  </span>
                )}
              </div>
              {recreateDialogState.status.key_usage && (
                <div className="text-xs" data-testid="recreate-key-usage">
                  <span className="text-zinc-500">Key usage this run: </span>
                  <span className="font-mono text-white">
                    {recreateDialogState.status.key_usage.map((count, idx) =>
                      `key#${idx}(${idx === 0 ? 'primary' : 'fallback'})=${count}`
                    ).join(' · ')}
                  </span>
                </div>
              )}
              {recreateDialogState.status.used_force_bypass !== undefined && (
                <div className="text-xs text-zinc-500">
                  Force bypass used: <span className="text-white">{String(recreateDialogState.status.used_force_bypass)}</span>
                </div>
              )}
              {recreateDialogState.status.error && (
                <div className="text-xs text-red-400 border-l-2 border-red-500 pl-3 py-1 bg-red-500/5">
                  <div className="font-semibold mb-1">Error:</div>
                  {recreateDialogState.status.error}
                </div>
              )}
              {recreateDialogState.status.started_at && (
                <div className="text-xs text-zinc-500">
                  Started: {new Date(recreateDialogState.status.started_at).toLocaleString()}
                  {recreateDialogState.status.completed_at && (
                    <> · Completed: {new Date(recreateDialogState.status.completed_at).toLocaleString()}</>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Force toggle */}
          <div className="flex items-center gap-3 p-3 rounded-md bg-red-500/5 border border-red-500/20">
            <input
              type="checkbox"
              id="force-recreate"
              checked={recreateDialogState.force}
              onChange={(e) => setRecreateDialogState((prev) => ({ ...prev, force: e.target.checked }))}
              className="accent-red-500"
              data-testid="recreate-force-toggle"
            />
            <label htmlFor="force-recreate" className="text-sm text-zinc-200 cursor-pointer">
              <span className="text-red-300 font-semibold">Force overwrite</span> — bypass safety guard (allows &gt;25% drop)
              <div className="text-xs text-zinc-500 mt-0.5">
                Use only if the current DB is corrupted with bad data and you need to force a fresh fetch.
              </div>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecreateDialogOpen(false)} className="border-zinc-700">
              Close
            </Button>
            <Button
              onClick={startRecreate}
              disabled={recreateDialogState.status?.status === 'processing'}
              className={recreateDialogState.force ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-white text-black hover:bg-zinc-200'}
              data-testid="recreate-start-btn"
            >
              {recreateDialogState.force ? 'Force recreate' : 'Recreate (safe)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}